import { useLayoutEffect, useRef } from 'react'
import { gsap, registerGsap } from '../../anim/gsapSetup'
import { splitToLines, whenFontsReady, type SplitResult } from '../../anim/textReveal'
import { heroIntro } from '../../anim/heroTimeline'
import { useReducedMotion } from '../../anim/useReducedMotion'
import { Button } from '../common/Button'
import { Icon } from '../common/Icon'
import { Eyebrow } from '../common/Eyebrow'

const HERO_STATS = [
  { value: '10', label: 'Bio-input products' },
  { value: '5', label: 'Solution categories' },
  { value: 'NPOP · FCO', label: 'Approved range' },
  { value: 'Made in', label: 'India' },
]

const HERO_ALT =
  'An Indian farmer tending a thriving green cotton field at sunrise, with misty hills, palm trees and a village home in the background — GreenbayBio.'

// Portrait devices (phones + portrait tablets) get the portrait crop; landscape tablets and desktop
// get the wide crops. Kept in sync with the index.html LCP preloads.
const PORTRAIT_MEDIA = '(max-width: 767px), (max-width: 1023px) and (orientation: portrait)'
const TABLET_MEDIA = '(min-width: 768px) and (max-width: 1023px) and (orientation: landscape)'

export function Hero() {
  const scopeRef = useRef<HTMLDivElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const reduced = useReducedMotion()

  useLayoutEffect(() => {
    const scope = scopeRef.current
    const heading = headingRef.current
    if (!scope || !heading) return
    registerGsap()
    let split: SplitResult | null = null

    const ctx = gsap.context((self) => {
      if (reduced) {
        heroIntro({ scope, reduced: true })
        return
      }

      // Base entrance runs synchronously: sets hidden start-states before first paint (no flash),
      // and keeps the LCP image transform-only (never opacity:0).
      heroIntro({ scope, reduced: false })

      // Scroll parallax — created synchronously so gsap.context records + kills these ScrollTriggers
      // on unmount (the async font path would otherwise leak them).
      gsap.to('[data-hero="image"]', {
        yPercent: 8,
        ease: 'none',
        scrollTrigger: { trigger: scope, start: 'top top', end: 'bottom top', scrub: true },
      })
      gsap.to('[data-hero="content"]', {
        yPercent: 6,
        opacity: 0.25,
        ease: 'none',
        scrollTrigger: { trigger: scope, start: 'top top', end: 'bottom top', scrub: true },
      })

      // The heading needs real font metrics to measure line breaks — hide it now (so it never paints
      // then re-hides), then split + reveal once fonts are ready. self.add() keeps the split reveal
      // inside the gsap context so it is reverted on unmount.
      gsap.set(heading, { autoAlpha: 0 })
      const cancel = whenFontsReady(() =>
        self.add(() => {
          gsap.set(heading, { autoAlpha: 1 })
          split = splitToLines(heading)
          gsap.from(split.targets, {
            yPercent: 118,
            duration: 0.95,
            ease: 'power4.out',
            stagger: 0.1,
            delay: 0.35,
          })
        }),
      )
      return () => {
        cancel()
        split?.revert()
      }
    }, scope)
    return () => ctx.revert()
  }, [reduced])

  return (
    <section
      ref={scopeRef}
      aria-label="GreenbayBio introduction"
      className="relative flex min-h-[100svh] items-start overflow-hidden bg-night text-white landscape:items-center"
    >
      {/* ── Full-bleed farmer photography (art-directed by size + orientation) ── */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Fallback brand wash — visible until the hero photography is added (or if it fails to load),
            so the hero always reads as an intentional dark editorial section, never a broken image. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[radial-gradient(120%_100%_at_75%_12%,rgba(21,154,60,0.30),transparent_58%),radial-gradient(90%_80%_at_15%_90%,rgba(10,64,34,0.6),transparent_70%)]"
        />
        <picture>
          <source media={PORTRAIT_MEDIA} type="image/webp" srcSet="/images/hero/greenbay-hero-mobile.webp" />
          <source media={PORTRAIT_MEDIA} srcSet="/images/hero/greenbay-hero-mobile.png" />
          <source media={TABLET_MEDIA} type="image/webp" srcSet="/images/hero/greenbay-hero-tablet.webp" />
          <source media={TABLET_MEDIA} srcSet="/images/hero/greenbay-hero-tablet.png" />
          <source type="image/webp" srcSet="/images/hero/greenbay-hero-desktop.webp" />
          <img
            data-hero="image"
            src="/images/hero/greenbay-hero-desktop.png"
            alt={HERO_ALT}
            // @ts-expect-error fetchpriority is valid HTML
            fetchpriority="high"
            decoding="async"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            className="absolute left-0 top-[-10%] h-[120%] w-full object-cover object-[72%_center]"
          />
        </picture>
      </div>

      {/* ── Legibility + brand scrims (matched to image orientation) ── */}
      <div data-hero="scrim" className="absolute inset-0">
        <div className="absolute inset-0 bg-night/25" />
        {/* portrait: dark top for text over the sky */}
        <div className="absolute inset-0 bg-gradient-to-b from-night/85 via-night/15 to-transparent landscape:hidden" />
        {/* landscape: dark left, subject stays visible on the right */}
        <div className="absolute inset-0 hidden bg-gradient-to-r from-night/92 via-night/55 to-transparent landscape:block" />
        {/* top scrim keeps the transparent navbar legible */}
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-night/55 to-transparent" />
        {/* bottom fade melts the photo into the next (light) section */}
        <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-b from-transparent to-canvas md:h-32" />
      </div>

      {/* ── Content ── */}
      <div
        data-hero="content"
        className="container-x relative z-10 w-full pb-28 pt-[calc(var(--nav-h)+2.5rem)] landscape:py-[var(--nav-h)]"
      >
        <div className="max-w-2xl">
          <div className="mb-6">
            <span data-hero="eyebrow">
              <Eyebrow tone="light" withDot={false}>
                Sustainable Bio-Innovation
              </Eyebrow>
            </span>
          </div>

          <h1
            ref={headingRef}
            className="font-display text-display-2xl font-semibold leading-[1.02] text-white text-shadow-hero [text-wrap:normal]"
          >
            Stronger crops start in living soil.
          </h1>

          <p data-hero="lead" className="mt-6 max-w-xl text-lead text-white/90 text-shadow-hero">
            GreenbayBio makes bio-fertilizers, biostimulants and plant-strength solutions that rebuild the
            soil beneath India’s farms — so crops grow more resilient, the natural way.
          </p>

          <div data-hero="cta" className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Button to="/catalog" variant="primary" size="lg" className="w-full sm:w-auto">
              Explore products <Icon name="ArrowRight" className="h-5 w-5" />
            </Button>
            <Button to="/crop-solutions" variant="ghost-light" size="lg" className="w-full sm:w-auto">
              Find crop solutions
            </Button>
          </div>

          <dl data-hero="meta" className="mt-12 hidden max-w-lg gap-x-6 gap-y-5 lg:grid lg:grid-cols-4">
            {HERO_STATS.map((s) => (
              <div key={s.label}>
                <dt className="font-display text-2xl font-semibold text-white text-shadow-hero">{s.value}</dt>
                <dd className="mt-0.5 text-xs uppercase tracking-[0.12em] text-white/75 text-shadow-hero">{s.label}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {/* ── Scroll cue (sits over the light bottom fade, so it is dark for contrast) ── */}
      <div data-hero="cue" className="absolute inset-x-0 bottom-6 z-10 flex flex-col items-center gap-2 text-ink-700">
        <span className="text-[0.65rem] font-semibold uppercase tracking-[0.3em]">Scroll</span>
        <span className="flex h-9 w-5 items-start justify-center rounded-full border border-ink/30 p-1">
          <span className="h-2 w-1 animate-float-y rounded-full bg-ink-500" />
        </span>
      </div>
    </section>
  )
}
